/**
 * Geometry for the branded teardrop, exported from the map assets in the NOP master design
 * file.
 *
 * Kept apart from the component partly so the markup stays readable, and partly because the
 * legacy web map in `workspaces/frontend/src/hooks/use-map` draws the same pin through Google
 * Maps and reads these paths too. Nothing here may import anything, so either platform can
 * pick it up without dragging the other's map library along.
 */

/** The artwork's own box, and the point on it that sits on the store's coordinate. */
export const PIN_ART_WIDTH = 40;
export const PIN_ART_HEIGHT = 62;
export const PIN_ART_TIP = { x: 20, y: 62 };

export const PIN_TEARDROP_PATH =
  'M19.9993 0C8.95232 0 0 8.25428 0 18.4324C0 28.6131 19.9993 62 19.9993 62C19.9993 62 40 ' +
  '28.6131 40 18.4324C40 8.25428 31.042 0 19.9993 0Z';

export const PIN_REGISTERED_MARK_PATH =
  'M30.8721 29.1757C30.8721 29.5899 30.5385 29.9179 30.117 29.9179C29.6954 29.9179 29.3564 ' +
  '29.5899 29.3564 29.1757C29.3564 28.7615 29.6936 28.4389 30.1207 28.4389C30.5477 28.4389 ' +
  '30.8721 28.7633 30.8721 29.1757ZM29.5452 29.1757C29.5452 29.5037 29.7926 29.764 30.1207 ' +
  '29.764C30.4396 29.7677 30.6833 29.5037 30.6833 29.1793C30.6833 28.8549 30.4396 28.5855 ' +
  '30.1115 28.5855C29.7834 28.5855 29.5452 28.8513 29.5452 29.1738V29.1757ZM29.9997 ' +
  '29.5587H29.8292V28.8219C29.8971 28.8128 29.9905 28.7999 30.1133 28.7999C30.2526 28.7999 ' +
  '30.3149 28.8219 30.3644 28.8586C30.4084 28.8897 30.4414 28.9447 30.4414 29.0162C30.4414 ' +
  '29.106 30.3736 29.1647 30.2893 29.1922V29.2013C30.3607 29.2233 30.3974 29.282 30.4194 ' +
  '29.3809C30.4414 29.4927 30.4597 29.5331 30.4725 29.5606H30.2929C30.2709 29.5331 30.2563 ' +
  '29.4707 30.2343 29.3809C30.2215 29.304 30.1756 29.2691 30.0822 29.2691H30.0015V29.5624L29.9997 ' +
  '29.5587ZM30.0034 29.1445H30.084C30.1793 29.1445 30.2544 29.1134 30.2544 29.0364C30.2544 ' +
  '28.9686 30.205 28.9246 30.0968 28.9246C30.051 28.9246 30.0199 28.9282 30.0034 28.9337V29.1445Z';

/** The "F" monogram inside the badge. */
export const PIN_MONOGRAM_PATH =
  'M20.0177 10.6797C24.6217 10.6797 28.3539 14.4119 28.3539 19.0159C28.3539 23.6199 24.6217 ' +
  '27.3521 20.0177 27.3521C15.4138 27.3521 11.6815 23.6199 11.6815 19.0159C11.6815 14.4119 ' +
  '15.4138 10.6797 20.0177 10.6797ZM24.0743 13.4595C24.0541 13.6721 24.0285 13.8095 23.9918 ' +
  '13.8774C23.9551 13.9452 23.8983 13.9818 23.825 13.9818H16.7963V14.2036C16.9704 14.2292 ' +
  '17.0785 14.2696 17.1207 14.3282C17.1592 14.3814 17.1793 14.4547 17.1793 14.5427L17.194 ' +
  '24.1007C17.194 24.1868 17.1738 24.2565 17.1335 24.3151C17.0932 24.3719 16.9081 24.4324 ' +
  '16.734 24.4599V24.8173L19.6793 24.799V24.4544C19.5052 24.4288 19.397 24.3243 19.3548 ' +
  '24.2675C19.3145 24.2106 19.2925 24.1263 19.2925 24.0402V20.0484L22.4724 20.074C22.5476 ' +
  '20.074 22.6007 20.096 22.6392 20.1583C22.6777 20.2206 22.7034 20.2078 22.7253 ' +
  '20.4186H22.8885V17.2919H22.7253C22.7052 17.4659 22.6777 17.5814 22.6392 17.6364C22.6026 ' +
  '17.6932 22.5476 17.7922 22.4724 17.7922L19.2925 17.7959V16.0785L23.825 16.0987C23.9002 ' +
  '16.0987 23.9551 16.128 23.9918 16.1903C24.0284 16.2545 24.0541 16.3718 24.0743 ' +
  '16.5441H24.2392V13.4595H24.0743Z';

/** The scalloped badge outline the monogram sits in. */
export const PIN_BADGE_PATH =
  'M20.0403 6C21.6678 6.00184 22.6466 6.55534 23.6674 7.2243C24.9137 8.03988 25.5332 8.32581 ' +
  '26.5137 8.29649C26.6145 8.29282 26.7172 8.28734 26.8198 8.28184C27.4759 8.24519 27.8957 ' +
  '8.25068 28.1798 8.65572C28.3099 8.83717 28.5774 9.21843 27.8187 10.3987L27.1185 ' +
  '11.4874L27.5621 11.9309L28.6013 11.2619C29.7798 10.5032 30.161 10.7726 30.3443 ' +
  '10.9009C30.7493 11.185 30.7567 11.6047 30.7182 12.2609C30.7127 12.3653 30.7072 12.4661 ' +
  '30.7035 12.5669C30.6742 13.5493 30.9582 14.1669 31.7757 15.4132C32.4446 16.4341 33 17.4128 ' +
  '33 19.0404C32.9982 20.6679 32.4446 21.6466 31.7757 22.6674C30.9601 23.9137 30.6742 24.5314 ' +
  '30.7035 25.5138C30.7072 25.6146 30.7127 25.7172 30.7182 25.8199C30.7548 26.476 30.7493 ' +
  '26.8939 30.3443 27.1798C30.1628 27.3099 29.7816 27.5775 28.6013 26.8187L27.5126 ' +
  '26.1186L27.0691 26.5621L27.738 27.6013C28.4968 28.7798 28.2274 29.161 28.0991 ' +
  '29.3443C27.815 29.7493 27.3953 29.7567 26.7392 29.7182C26.6347 29.7127 26.5339 29.7072 ' +
  '26.4331 29.7035C25.4489 29.6724 24.8331 29.9583 23.5868 30.7757C22.5659 31.4447 21.5872 32 ' +
  '19.9597 32C18.3322 31.9982 17.3534 31.4447 16.3326 30.7757C15.1431 29.9968 14.5273 29.7017 ' +
  '13.6182 29.7017C13.5743 29.7017 13.5303 29.7035 13.4863 29.7035C13.3855 29.7072 13.2828 ' +
  '29.7127 13.1802 29.7182C12.5241 29.7549 12.1043 29.7493 11.8202 29.3443C11.6901 29.161 ' +
  '11.4225 28.7816 12.1813 27.6013L12.8815 26.5127L12.4379 26.0691L11.3987 26.7381C10.2203 ' +
  '27.4968 9.839 27.2274 9.65572 27.0991C9.25068 26.815 9.24335 26.3953 9.28184 ' +
  '25.7392C9.28733 25.6366 9.29282 25.5339 9.29649 25.4331C9.32581 24.4507 9.04172 23.8331 ' +
  '8.2243 22.5868C7.55533 21.5659 7 20.5872 7 18.9597C7.00183 17.3322 7.55534 16.3535 8.2243 ' +
  '15.3326C9.03989 14.0863 9.32581 13.4686 9.29649 12.4862C9.29282 12.3855 9.28733 12.2828 ' +
  '9.28184 12.1802C9.24518 11.524 9.25068 11.1062 9.65572 10.8203C9.839 10.6901 10.2184 ' +
  '10.4226 11.3987 11.1813L12.4874 11.8815L12.9309 11.4379L12.2619 10.3987C11.5032 9.22023 ' +
  '11.7726 8.839 11.9009 8.65572C12.185 8.25069 12.6047 8.24335 13.2608 8.28184C13.3635 ' +
  '8.28734 13.4661 8.29282 13.5669 8.29649C14.5474 8.32581 15.1669 8.04172 16.4132 ' +
  '7.2243C17.4341 6.55533 18.4128 6 20.0403 6ZM20.0177 9.67966C14.8615 9.67967 10.6815 13.8596 ' +
  '10.6815 19.0159C10.6815 24.152 14.8289 28.3195 19.9574 28.352L20.0177 28.3522C25.1538 ' +
  '28.3522 29.3213 24.2048 29.3538 19.0763L29.354 19.0159C29.354 13.8596 25.174 9.67966 ' +
  '20.0177 9.67966Z';
